/* global jobListImpressionUrl, applyRequiresUpdateUrl, updateFromOverridesUrl -- provided by the page (a Blade-injected value or a third-party script tag), not declared in this file */
import * as Sentry from '@sentry/browser';
import axios from 'axios';
import 'readmore-js';
import { warning } from '@common/alert';
import { confirm } from '@common/confirm';
import Expandable from '@common/expandable';
import { getLanguages } from '@common/language';
import TagManager from '@common/tagmanager';

/**
 * @typedef JobObject
 * @property {int} jobId
 * @property {boolean} directAppsEligible
 * @property {int} listPosition
 * @property {int} listDepth
 * @property {boolean} preselected
 */

/**
 * @typedef JobListConfiguration
 * @property {string} source
 * @property {number} [perPage]
 * @property {number} [testLeadId]
 * @property {string} recommenderType
 * @property {?string} [nextCursor]
 * @property {?boolean} [hasMoreRecommendations]
 * @property {?string} [funnelSlug]
 * @property {boolean} [showDetailsOnActivation]
 * @property {number} sessionLimit
 * @property {boolean} [wantsPromotedJobs]
 * @property {boolean} [wantsNonPromotedJobs]
 */

/**
 * @typedef RecommendedJobsRes
 * @type {object}
 * @property {string[]} jobs - Job markup.
 * @property {int[]} job_ids - Job Ids.
 * @property {boolean} hasMoreRecommendations - Number of jobs left to recommend.
 * @property {string|null} nextCursor
 */

/**
 * @typedef ApplyToJobsResResult
 * @type {object}
 * @property {boolean} accepted
 * @property {boolean} rejected
 */

/**
 * @typedef ApplyToJobsRes
 * @type {object}
 * @property {int} count - Number of jobs that were attempted to apply to.
 * @property {Object.<number, ApplyToJobsResResult>} results - Application result per job attempted.
 * @property {string[]} jobs - Job markup.
 * @property {int[]} job_ids - Job Ids.
 * @property {boolean} hasMoreRecommendations - Number of jobs left to recommend.
 * @property {string|null} nextCursor
 * @property {object[]} acceptedAttemptsConversions - Conversions for accepted attempts.
 */

/**
 * @typedef ApplyContext
 * @type {object}
 * @property {string} listPosition
 * @property {string} listDepth
 */

/**
 * @typedef ApplyToJobReq
 * @type {Object}
 * @property {number[]} recommended_jobs - IDs of jobs to apply to.
 * @property {string} source - Source of the application.
 * @property {string} recommender - Type of recommender used.
 * @property {string} [funnelSlug] - If in the funnel, the funnel slug.
 * @property {Object.<number, ApplyContext>} context - Contextual information about the application.
 */

/**
 * Selection Changed Event
 *
 * @event JobList#job-recommender:list:selection-changed
 * @type {object}
 * @property {JobObject[]} selected - The selected jobs.
 * @property {boolean} userInitiated - Whether the event was user initiated, if false it was performed programmatically.
 */

/**
 * Job Active Event
 *
 * @event JobList#job-recommender:list:active
 * @type {object}
 * @property {int} jobId - The ID of the job that was activated.
 * @property {string} jobViewUrl - The URL for registering a job view for this active job
 */

/**
 * Retrieving Jobs Event
 *
 * @event JobList#job-recommender:list:retrieving
 * @type {object}
 * @property {int} maxJobs - The number of jobs "perPage".. This is effectively a maximum, as the API may return fewer.
 */

/**
 * Retrieved Jobs Event
 *
 * @event JobList#job-recommender:list:retrieved
 * @type {object}
 * @property {int} numberOfJobs - The number of jobs that were retrieved.
 * @property {boolean} hasMoreJobs - Whether there are more jobs to retrieve.
 */

/**
 * Apply Event
 *
 * @event job-recommender:apply
 */

/**
 * Attempting Apply Event
 * Event fired before the apply request is sent.
 * @event JobList#job-recommender:list:attempting-apply
 */

/**
 * Apply Success Event
 * Event fired after the apply request is successful.
 * @event JobList#job-recommender:apply-success
 */

/**
 * No recommendations for the candidate, or the job request failed with nothing rendered
 *
 * @event JobList#job-recommender:list:recommendations-unavailable
 */

/**
 * One or more recommendations for the candidate
 *
 * @event JobList#job-recommender:list:recommendations-available
 */

/**
 * Override Recommendations
 *
 * @event JobList#job-recommender:override-jobs
 * @type {object}
 * @property {array} destinations - A list of destinations to use in the recommendation criteria
 * @property {int|null} license - License type to use in the recommendation criteria
 * @property {int|null} specialty - Specialty to use in the recommendation criteria
 */

/**
 * @class JobList
 * @fires JobList#job-recommender:list:selection-changed
 * @fires JobList#job-recommender:list:active
 * @fires JobList#job-recommender:list:retrieving
 * @fires JobList#job-recommender:list:retrieved
 * @fires JobList#job-recommender:list:recommendations-unavailable
 * @fires JobList#job-recommender:list:recommendations-available
 * @listens job-recommender:list:more-jobs
 */
class JobList {
    /** @type {string} */
    wrapperClassName = 'job-recommender-job-list';
    cardClassName = 'recommended-job';
    cardSelectionClassName = 'recommended-job-selection';
    assignmentDetailsClassName = 'recommended-job-detail';
    fetchingJobs = false;
    showDetailsOnActivation = false;

    /** @type {?string} */
    nextCursor = null;

    /** @type {boolean} */
    canFetchMoreJobs = true;

    /** @type {?string} */
    funnelSlug = null;

    /**
     * Jobs for which impressions were already created
     * @type {number[]}
     */
    impressionCache = [];

    sessionLimit = 1000;
    jobWord = 'Job';
    wantsPromotedJobs = true;
    wantsNonPromotedJobs = true;

    overrides = {};

    /**
     * @param {JobListConfiguration} configuration
     * @param configuration
     */
    constructor(configuration) {
        this.listElement = document.querySelector(`.${this.wrapperClassName}`);
        this.loadingIndicator = document.getElementById('job-list-loading-indicator');
        this.endOfListIndicator = document.getElementById('job-list-no-more-jobs');
        this.jobsUrl = this.listElement.dataset.jobs;
        this.applyUrl = this.listElement.dataset.apply;
        const lang = getLanguages();
        if (lang.job ?? false) {
            this.jobWord = lang.job.charAt(0).toUpperCase() + lang.job.slice(1);
        }
        this.parseConfiguration(configuration);
    }

    /**
     * Parse and validate Job List config object.
     * @param {JobListConfiguration} configuration
     */
    parseConfiguration(configuration) {
        const required = ['source', 'recommenderType'];

        required.forEach((key) => {
            if (!Object.hasOwn(configuration, key)) {
                throw new Error(`Missing required configuration key: ${key}`);
            }
        });

        this.nextCursor = configuration.nextCursor ?? null;

        if (Object.hasOwn(configuration, 'hasMoreRecommendations')) {
            this.endOfListState(!configuration.hasMoreRecommendations);
        }

        this.source = configuration.source;
        this.recommenderType = configuration.recommenderType;
        this.perPage = configuration.perPage ?? 20;
        this.testLeadId = configuration.testLeadId ?? null;
        this.funnelSlug = configuration.funnelSlug ?? null;
        this.jobId = configuration.jobId ?? null;
        this.showDetailsOnActivation = configuration.showDetailsOnActivation ?? false;
        this.sessionLimit = configuration.sessionLimit ?? 1000;
        this.wantsPromotedJobs = configuration.wantsPromotedJobs ?? true;
        this.wantsNonPromotedJobs = configuration.wantsNonPromotedJobs ?? true;
    }

    /**
     * If .recommended-job elements exist within the job list already, initialize the job list from the DOM.
     * This is used to ensure that the job list is properly initialized when it is rendered server-side.
     * @fires JobList#job-recommender:list:selection-changed
     * @fires JobList#job-recommender:list:recommendations-available
     */
    initFromDOM() {
        const dataset = this.listElement.dataset;

        if (!Object.hasOwn(dataset, 'recommenderType')) {
            throw new Error('Missing required configuration key when initializing job list from the DOM: recommenderType');
        }

        this.recommenderType = dataset.recommenderType;

        if (!Object.hasOwn(dataset, 'initialCursor')) {
            throw new Error('Missing required configuration key when initializing job list from the DOM: initialCursor');
        }

        this.nextCursor = dataset.initialCursor;

        if (Object.hasOwn(dataset, 'jobId')) {
            this.jobId = dataset.jobId;
        }

        if (Object.hasOwn(dataset, 'showDetailsOnActivation')) {
            this.showDetailsOnActivation = true;
        }

        const hasMoreRecs = Boolean(dataset.hasMoreRecommendations);

        this.fireRecommendationsAvailable();
        this.fireSelectionChanged(false);
        this.loadingState(false);

        this.endOfListState(!hasMoreRecs && !this.nextCursor);

        this.handleActivatingPreRenderedJob();

        this.createJobImpressions();

        this.setUpEventListeners();
    }

    /**
     * Initialize the job list from a provided array of job markup.
     * @param {string[]} jobCardMarkup
     */
    initFromMarkup(jobCardMarkup) {
        const hasJobs = jobCardMarkup.length > 0;
        if (hasJobs) {
            this.renderJobsMarkup(jobCardMarkup);

            this.fireRecommendationsAvailable();
            this.fireSelectionChanged(false);
        } else {
            this.loadingState(false);
        }

        this.loadingState(false);
        this.endOfListState(!hasJobs);

        this.handleActivatingPreRenderedJob();

        this.createJobImpressions();

        this.setUpEventListeners();
    }

    isMobileExperience() {
        return window.innerWidth <= 768;
    }

    /**
     * When jobs are handed over either via markup or rendered in the dom ahead of time,
     * we need to handle jobs which are already marked as active.
     */
    handleActivatingPreRenderedJob() {
        const $activeJobs = $(`.${this.cardClassName}.active`);
        if ($activeJobs.length > 0) {
            const firstActiveJob = $(`.${this.cardClassName}.active`).get(0);
            /**
             * This will activate the job, fire the appropriate event, and de-activate any _other_ active jobs.
             * This is important because we need to ensure that only one job is active at a time.
             * Not using this.activateJob() is intentional here, because that will short circuit if the job is already
             * active, and also auto-scroll to the job, which we do not want to do if the job is pre-rendered as active.
             */
            $activeJobs.removeClass('active');
            firstActiveJob.classList.add('active');
            this.fireJobActive(firstActiveJob, this.parseJobCardElementToObject(firstActiveJob));
        }
    }

    /**
     * Initialize the JobList listeners and core functionality.
     */
    init() {
        /**
         * Fetch the initial jobs to display if there were not any already rendered.
         */
        this.retrieveJobs()
            .then((res) => {
                if (res && res.jobs.length === 0 && res.nextCursor === null && !res.hasMoreRecommendations) {
                    /**
                     * If we have to fetch jobs, and none exist to be recommended,
                     * we need to fire this event to let the outside world know that there are no recommendations.
                     */
                    this.fireRecommendationsUnavailable();
                }

                if (res && res.jobs.length > 0) {
                    this.fireRecommendationsAvailable();
                }

                return res;
            })
            .finally(() => {
                if (this.getSelectedJobObjects().length > 0) {
                    /**
                     * This is required to ensure any jobs which were pre-selected are visible to listeners of the
                     * selection-changed event.
                     */
                    this.fireSelectionChanged(false);
                }

                this.createJobImpressions();
            });

        this.setUpEventListeners();
    }

    createJobImpressions() {
        const jobIds = this.getAllJobObjects()
            .map(obj => obj.jobId)
            .filter(id => !this.impressionCache.includes(id));

        if (jobIds.length) {
            axios.post(
                jobListImpressionUrl,
                {
                    jobIds: jobIds,
                },
            )
                .then(() => {
                    this.impressionCache = [
                        ...this.impressionCache,
                        ...jobIds,
                    ];
                });
        }
    }

    /**
     * Appropriately set values when we are loading jobs.
     * @param {boolean} isLoading
     */
    loadingState(isLoading) {
        this.fetchingJobs = isLoading;
        this.loadingIndicator.style.display = isLoading ? 'block' : 'none';

        if (isLoading) {
            this.endOfListState(false);
        }
    }

    endOfListState(isEndOfList) {
        if (isEndOfList) {
            this.fetchingJobs = false;
            this.canFetchMoreJobs = false;
            this.loadingState(false);
            this.endOfListIndicator.style.display = 'block';
        } else {
            this.endOfListIndicator.style.display = 'none';
        }
    }

    /**
     * Render the job markup to the DOM.
     * @param {string[]} jobsMarkup
     */
    renderJobsMarkup(jobsMarkup) {
        jobsMarkup.forEach((job) => {
            this.loadingIndicator.insertAdjacentHTML('beforebegin', job);
        });
    }

    /**
     * Get the JobObject representation for each job.
     * @returns {JobObject[]}
     */
    getAllJobObjects() {
        return Array.from(
            document.querySelectorAll(`.${this.wrapperClassName} .${this.cardSelectionClassName}`),
        )
            .map((el) => {
                /** @type {HTMLElement} el */
                return this.parseJobCardElementToObject(el.closest(`.${this.cardClassName}`));
            });
    }

    /**
     * Get the JobObject representation for each of the selected jobs.
     * @returns {JobObject[]}
     */
    getSelectedJobObjects() {
        return Array.from(
            document.querySelectorAll(`.${this.wrapperClassName} .${this.cardSelectionClassName}:checked`),
        )
            .map((el) => {
                /** @type {HTMLElement} el */
                return this.parseJobCardElementToObject(el.closest(`.${this.cardClassName}`));
            });
    }

    /**
     * Toggles if a job is selected and ready to be applied to based on the user input.
     * @fires JobList#job-recommender:list:selection-changed
     * @fires JobList#job-recommender:list:active
     * @param {HTMLElement} cardElement
     */
    toggleJobSelection(cardElement) {
        if (this.getSelectedJobObjects().length >= this.sessionLimit) {
            return;
        }
        const input = $(cardElement).find('input');
        input.prop('checked', !input.prop('checked'));
        this.activateJob(cardElement);

        this.fireSelectionChanged();
    }

    /**
     * Close all open job detail cards which appear in the job list. This is used to ensure that only one job detail.
     */
    closeAllJobDetails() {
        const expandedElements = document.querySelectorAll(`.${this.cardClassName}.expanded`);
        Array.from(expandedElements).forEach(el => el.classList.remove('expanded'));
    }

    /**
     * Open the job details which are visible on mobile.
     * @param {HTMLElement} element
     */
    toggleMobileJobDetails(element) {
        const initiallyOpen = element.classList.contains('expanded');
        this.closeAllJobDetails();

        if (initiallyOpen) {
            element.classList.remove('expanded');
        } else {
            element.classList.add('expanded');
        }

        Expandable.init();
    }

    /**
     * Activate a job card, and ensure that it is the only active job card.
     * @fires JobList#job-recommender:list:active
     * @param {HTMLElement} cardElement
     * @param {boolean} force - Ordinarily, we only want to trigger the active job event when the job is not already active. With force = true, the class check will be bypassed.
     */
    activateJob(cardElement, force = false) {
        if (!force && cardElement.classList.contains('active')) {
            // The job is already active, and we should not need to do anything further.
            return;
        }

        const $list = $(this.listElement);

        $list.animate({
            scrollTop: $(cardElement).offset().top - $list.offset().top + $list.scrollTop(),
        });

        const $activeElements = $(`.${this.cardClassName}.active`);
        $activeElements.removeClass('active');

        cardElement.classList.add('active');

        this.fireJobActive(cardElement, this.parseJobCardElementToObject(cardElement));
    }

    /**
     * Remove all jobs from the list, and fire selection change to notify listeners.
     */
    clearAllJobs() {
        $(this.listElement).find('.job-recommender-card-header').remove();
        $(this.listElement).find('.job-recommender-no-results-section').remove();
        $(this.listElement).find('hr').remove();
        $(this.listElement).find(`.${this.cardClassName}`).remove();
        $(this.listElement).find(`.${this.assignmentDetailsClassName}`).remove();

        /**
         * This needs to be fired because the actual jobs are removed from the DOM, thus there are no selected jobs.
         */
        this.fireSelectionChanged(false);
    }

    /**
     * Get the current depth of the list. Used for application context tags.
     * @returns {number}
     */
    getCurrentListDepth() {
        return $(this.listElement).find(`.${this.cardClassName}`).length;
    }

    /**
     * Parse the dataset of the element to an object to be easily accessed elsewhere.
     * @param {HTMLElement} cardElement
     * @returns {JobObject}
     */
    parseJobCardElementToObject(cardElement) {
        return {
            jobId: parseInt(cardElement.dataset.jobId),
            listPosition: parseInt(cardElement.dataset.listPosition),
            directAppsEligible: Boolean(cardElement.dataset.jobDaEligible),
            listDepth: this.getCurrentListDepth(),
            preselected: Boolean(cardElement.dataset.preselected),
        };
    }

    /**
     * Create a context object that can be sent along with the apply request.
     * @returns {Object.<number, ApplyContext>}
     */
    getContextForSelectedJobs() {
        const contexts = {};

        this.getSelectedJobObjects().forEach((job) => {
            contexts[job.jobId] = {
                listPosition: job.listPosition,
                listDepth: job.listDepth,
                preselected: job.preselected,
                directAppsEligible: job.directAppsEligible,
            };
        });

        return contexts;
    }

    /**
     * Retrieve more jobs and appropriately set the class properties based on the response.
     * @fires JobList#job-recommender:list:retrieving
     * @fires JobList#job-recommender:list:retrieved
     * @fires JobList#job-recommender:list:recommendations-unavailable
     * @returns {Promise<?RecommendedJobsRes>}
     */
    async retrieveJobs() {
        this.fireRetrievingJobs();

        if (this.fetchingJobs || !this.canFetchMoreJobs) {
            return Promise.resolve(null);
        }

        this.loadingState(true);

        const response = await axios.get(this.jobsUrl, {
            params: {
                source: this.source,
                perPage: this.perPage,
                lead: this.testLeadId,
                recommender: this.recommenderType,
                cursor: this.nextCursor,
                jobId: this.jobId,
                wantsPromotedJobs: this.wantsPromotedJobs,
                wantsNonPromotedJobs: this.wantsNonPromotedJobs,
                overrides: this.overrides,
                exploreMoreHeaderShown: this.exploreMoreHeaderShown(),
            },
        }).catch((error) => {
            console.error('Unable to load recommended jobs.', error);

            // Network errors and aborts are noise; only server errors are actionable.
            if (error.response?.status >= 500) {
                Sentry.captureException(error);
            }

            return null;
        });

        if (!response) {
            this.loadingState(false);

            // Same outcome as an empty result, so the null state and the apply funnel move on.
            if (this.getAllJobObjects().length === 0) {
                this.fireRecommendationsUnavailable();
            }

            return null;
        }

        const { data } = response;

        if (!data.hasMoreRecommendations) {
            this.endOfListState(true);
        }

        if (data.jobs.length > 0) {
            this.renderJobsMarkup(data.jobs);
        } else {
            this.renderJobsMarkup([]);
        }

        this.nextCursor = data.nextCursor;

        this.fireRetrievedJobs(
            data.jobs.length,
            data.hasMoreRecommendations,
        );

        this.loadingState(false);

        return Promise.resolve(data);
    }

    async applyToSelectedJobs() {
        const selectedJobs = this.getSelectedJobObjects();

        if (this.hasOverrides()) {
            const overrideCheck = await axios.post(applyRequiresUpdateUrl, { overrides: this.overrides });
            const { requiresUpdate, details } = overrideCheck.data;

            if (requiresUpdate) {
                try {
                    await confirm({
                        title: 'Update Information to Apply',
                        text: details,
                        confirmButtonText: 'Update & Apply',
                        cancelButtonText: 'Nevermind',
                        icon: null,
                    });
                } catch {
                    return;
                }

                try {
                    await axios.post(updateFromOverridesUrl, { overrides: this.overrides });
                } catch {
                    warning('Unable to update your information. Please try again later');
                    return;
                }

                this.fireProfileUpdatedFromOverride(this.overrides);
            }
        }

        /** @type {ApplyToJobReq} postData */
        const postData = {
            recommended_jobs: selectedJobs.map(job => job.jobId),
            source: this.source,
            recommender: this.recommenderType,
            context: this.getContextForSelectedJobs(),
            jobId: this.jobId,
            overrides: this.overrides,
            // This should explicitly be false because we want to be able to show the header again when we get a response.
            // Then entire list is re-rendered, so the header _can_ be shown again.
            exploreMoreHeaderShown: false,
        };

        this.fireAttemptingApply();

        if (this.funnelSlug) {
            postData.funnelSlug = this.funnelSlug;
        }

        this.fireRetrievingJobs();
        this.loadingState(true);

        await axios.post(this.applyUrl, postData)
            .then((res) => {
                /** @type {AxiosResponse<ApplyToJobsRes>} res*/
                this.clearAllJobs();

                const total = res.data.count;
                const results = Object.values(res.data.results);
                const accepted = results.filter(result => result.accepted).length;
                const rejected = results.filter(result => result.rejected).length;

                if (total === accepted) {
                    this.fireApplySuccess(accepted);
                } else if (total === rejected) {
                    this.fireApplyFailure(rejected);
                } else if (accepted > 0 && rejected > 0) {
                    this.fireApplyPartialSuccess(accepted, rejected);
                }

                if (res.data.acceptedAttemptsConversions && res.data.acceptedAttemptsConversions.length > 0) {
                    res.data.acceptedAttemptsConversions.forEach((conversion) => {
                        TagManager.applicationAccepted(conversion);
                    });
                }

                if (typeof res.data.remainingApplications !== 'undefined') {
                    this.sessionLimit = res.data.remainingApplications;
                }

                if (res.data.dailyLimitReached) {
                    this.fireRecommendationsUnavailable();
                    this.fireDailyLimitReached();
                } else if (res.data.jobs.length > 0) {
                    this.renderJobsMarkup(res.data.jobs);
                    this.nextCursor = res.data.nextCursor;
                    this.canFetchMoreJobs = res.data.hasMoreRecommendations;

                    if (!this.canFetchMoreJobs) {
                        this.endOfListState(true);
                    }

                    this.fireRetrievedJobs(res.data.jobs.length);
                    this.fireRecommendationsAvailable();
                } else {
                    this.fireRecommendationsUnavailable();
                }
            })
            .catch((err) => {
                if (err.response && err.response.status === 429) {
                    this.sessionLimit = 0;
                    this.fireRecommendationsUnavailable();
                    this.fireDailyLimitReached();
                } else {
                    warning(
                        'Something went wrong. Please try again later.',
                        'Error',
                    );
                }
            })
            .finally(() => {
                this.loadingState(false);
                this.fireSelectionChanged(false);
                this.createJobImpressions();
            });
    }

    /**
     * @return {boolean}
     */
    hasOverrides() {
        return Boolean(Object.keys(this.overrides).length);
    }

    /**************************************************************************/
    /* Job List Event Listener Set Up / Teardown */
    /**************************************************************************/

    /**
     * Set up list event listeners, and listeners for events that we need to react to.
     */
    setUpEventListeners() {
        const list = $(this.listElement);

        $(window).on('resize', () => {
            if (!this.isMobileExperience()) {
                /**
                 * Job detail cards should only be expanded on mobile devices.
                 */
                this.closeAllJobDetails();
            }
        });

        list
            .on('click', `.${this.cardClassName}`, (evt) => {
                if (this.isMobileExperience() && this.showDetailsOnActivation) {
                    this.toggleMobileJobDetails(evt.currentTarget);
                }

                this.activateJob(evt.currentTarget);

                if (evt.target.classList.contains('selection')) {
                    // If the click occurred in the selection area on the left, we should toggle the selection.
                    this.toggleJobSelection(evt.currentTarget);
                }
            })
            .on('click', '.mobile-details', (evt) => {
                if (!this.isMobileExperience()) {
                    /**
                     * Don't do the thing unless we're on a mobile device.
                     */
                    return;
                }

                const card = evt.currentTarget.closest(`.${this.cardClassName}`);
                this.toggleMobileJobDetails(card);
                this.activateJob(card, true);

                evt.stopPropagation();
            })
            .on('click', '.close-recommender-job-detail', (evt) => {
                const jobId = evt.currentTarget.closest(`.${this.assignmentDetailsClassName}`).dataset.jobId;
                const card = list.find(`.${this.cardClassName}[data-job-id="${jobId}"]`);
                card.removeClass('expanded');
            })
            .on('change', `.${this.cardSelectionClassName}`, () => {
                this.fireSelectionChanged();
            });

        $(document)
            .on('job-recommender:list:more-jobs', async () => {
                if (!this.fetchingJobs && this.canFetchMoreJobs) {
                    const res = await this.retrieveJobs();

                    if (res) {
                        this.createJobImpressions();
                    }
                }
            })
            .on('job-recommender:apply', async () => {
                if (this.getSelectedJobObjects().length > 0) {
                    await this.applyToSelectedJobs();
                }
            })
            .on('job-recommender:override-jobs', async (e) => {
                this.overrides = e.detail;
                this.nextCursor = null;
                this.canFetchMoreJobs = true;
                this.clearAllJobs();

                // This resets the viewer to the intitial state.
                this.fireRecommendationsAvailable();

                await this.retrieveJobs();

                if (this.getSelectedJobObjects().length > 0) {
                    /**
                     * This is required to ensure any jobs which were pre-selected are visible to listeners of the
                     * selection-changed event.
                     */
                    this.fireSelectionChanged(false);
                }

                this.createJobImpressions();
            });
    }

    /**
     * Determine if the explore more header was already shown.
     * @returns {boolean}
     */
    exploreMoreHeaderShown() {
        return this.listElement.querySelector('.job-recommender-card-header#explore-more') !== null;
    }

    /**
     * Teardown event listeners, and remove all elements from the wrapper.
     */
    teardown() {
        $(this.listElement).off('click', `.${this.cardClassName}`)
            .off('click', '.mobile-details')
            .off('click', '.close-recommender-job-detail')
            .off('change', `.${this.cardSelectionClassName}`);

        this.clearAllJobs();
        this.fireSelectionChanged(false);

        $(document).off('job-recommender:apply')
            .off('job-recommender:list:more-jobs');
    }

    /**************************************************************************/
    /* Event Firing Methods */
    /**************************************************************************/

    /**
     * @fires JobList#job-recommender:list:selection-changed
     * @param {boolean} [userInitiated=true] - Whether the selection change was made by the user. If false, it was automated.
     */
    fireSelectionChanged(userInitiated = true) {
        const event = new CustomEvent(
            'job-recommender:list:selection-changed',
            {
                bubbles: true,
                detail: {
                    selected: this.getSelectedJobObjects(),
                    userInitiated: userInitiated,
                },
            },
        );

        this.listElement.dispatchEvent(event);

        if (this.getSelectedJobObjects().length >= this.sessionLimit) {
            this.endOfListState(true);
            const $selector = $('.recommended-job-selection:not(:checked)');
            $selector.prop('disabled', true)
                .each(function() {
                    $(`label[for="${$(this).attr('id')}"] > span.unselected > i.mdi`).addClass('disabled');
                });
            $selector.parent().find('.selection').addClass('disabled');
            $selector.parent().find('.details > .job-pay').addClass('disabled');
        }
        if (this.getSelectedJobObjects().length < this.sessionLimit) {
            this.endOfListState(false);
            const $selector = $('.recommended-job-selection:not(:checked)');
            $selector.prop('disabled', false)
                .each(function() {
                    $(`label[for="${$(this).attr('id')}"] > span.unselected > i.mdi`).removeClass('disabled');
                });
            $selector.parent().find('.selection').removeClass('disabled');
            $selector.parent().find('.details > .job-pay').removeClass('disabled');
        }
    }

    /**
     * @param {HTMLElement} cardElement - The card which was activated.
     * @param {JobObject} jobObject
     * @fires JobList#job-recommender:list:active
     */
    fireJobActive(cardElement, jobObject) {
        const event = new CustomEvent(
            'job-recommender:list:active',
            {
                bubbles: true,
                detail: {
                    jobId: jobObject.jobId,
                    jobViewUrl: $(cardElement).data('job-view-url'),
                },
            },
        );

        cardElement.dispatchEvent(event);
    }

    /**
     * @fires JobList#job-recommender:list:retrieving
     */
    fireRetrievingJobs() {
        const event = new CustomEvent(
            'job-recommender:list:retrieving',
            {
                bubbles: true,
                detail: {
                    maxJobs: this.perPage,
                },
            },
        );

        this.listElement.dispatchEvent(event);
    }

    /**
     * @param {number} numJobs
     * @param {boolean} hasMore
     * @fires JobList#job-recommender:list:retrieved
     */
    fireRetrievedJobs(numJobs, hasMore) {
        const event = new CustomEvent(
            'job-recommender:list:retrieved',
            {
                bubbles: true,
                detail: {
                    numberOfJobs: numJobs,
                    hasMoreJobs: hasMore,
                },
            },
        );

        this.listElement.dispatchEvent(event);
    }

    /**
     * @fires JobList#job-recommender:list:recommendations-available
     */
    fireRecommendationsAvailable() {
        const evt = new CustomEvent(
            'job-recommender:list:recommendations-available',
            { bubbles: true },
        );

        this.listElement.dispatchEvent(evt);
    }

    /**
     * @fires JobList#job-recommender:list:recommendations-unavailable
     */
    fireRecommendationsUnavailable() {
        const evt = new CustomEvent(
            'job-recommender:list:recommendations-unavailable',
            { bubbles: true },
        );

        this.listElement.dispatchEvent(evt);
    }

    /**
     * @fires JobList#job-recommender:daily-limit-reached
     */
    fireDailyLimitReached() {
        const evt = new CustomEvent(
            'job-recommender:daily-limit-reached',
            { bubbles: true },
        );

        this.listElement.dispatchEvent(evt);
    }

    /**
     * @fires JobList#job-recommender:attempting-apply
     */
    fireAttemptingApply() {
        const evt = new CustomEvent(
            'job-recommender:attempting-apply',
            {
                bubbles: true,
                cancelable: true,
            },
        );

        this.listElement.dispatchEvent(evt);
    }

    /**
     * @param {number} numJobs
     * @fires JobList#job-recommender:apply-success
     */
    fireApplySuccess(numJobs) {
        const evt = new CustomEvent(
            'job-recommender:apply-success',
            { bubbles: true, detail: { numJobs } },
        );

        this.listElement.dispatchEvent(evt);
    }

    /**
     * @param {number} numJobs
     * @fires JobList#job-recommender:apply-failure
     */
    fireApplyFailure(numJobs) {
        const evt = new CustomEvent(
            'job-recommender:apply-failure',
            { bubbles: true, detail: { numJobs } },
        );

        this.listElement.dispatchEvent(evt);
    }

    /**
     * @param {number} accepted
     * @param {number} rejected
     * @fires JobList#job-recommender:apply-partial-success
     */
    fireApplyPartialSuccess(accepted, rejected) {
        const evt = new CustomEvent(
            'job-recommender:apply-partial-success',
            { bubbles: true, detail: { accepted, rejected } },
        );

        this.listElement.dispatchEvent(evt);
    }

    /**
     * @fires JobList#job-recommender:profile-updated-from-override
     */
    fireProfileUpdatedFromOverride(overrides) {
        const evt = new CustomEvent(
            'job-recommender:profile-updated-from-override',
            { bubbles: true, detail: { overrides } },
        );

        this.listElement.dispatchEvent(evt);
    }
}

export default JobList;
